import { RotateCcw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { DateCell } from '../../../../components/table/DateCell';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { DispatchedDocumentModal } from '../components/DispatchedDocumentModal';
import { dispatchedDocumentsApi, routingDocumentTypesApi } from '../routingApi';
import { DISPATCHING_PATH, ROUTING_SETTINGS_PATH, dispatchDeleteDisabledReason } from '../routingRules';
import { DISPATCHING_AUDIT_TYPES, type DispatchedDocumentDetail, type DispatchedDocumentListItem } from '../routingTypes';

/**
 * Dispatching — every document that went out, where to, and from which unit.
 *
 * **Newest first.** The source facet answers "what did we send on, and what did we write" in one
 * press; the `Sends On` column links the two registers by number without opening either.
 *
 * **No Clear data** — the module declares none, for the reason Receiving declares none.
 */
export function DispatchedDocumentsIndexPage() {
  const navigate = useNavigate();
  const toast = useToast();

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<DispatchedDocumentDetail | null>(null);
  const [confirming, setConfirming] = useState<{ kind: 'delete' | 'restore'; row: DispatchedDocumentListItem } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  // THE ONE REQUIRED CATALOG, seeded MET — see the Receiving page.
  const [hasType, setHasType] = useState(true);

  useEffect(() => {
    const controller = new AbortController();

    routingDocumentTypesApi
      .options(controller.signal)
      .then((options) => {
        if (!controller.signal.aborted) setHasType(options.length > 0);
      })
      .catch(() => {});

    return () => controller.abort();
  }, [reloadKey]);

  const fetchRows = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      dispatchedDocumentsApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
          documentType: query.facets.documentType?.join(','),
          medium: query.facets.medium?.join(','),
          mode: query.facets.mode?.join(','),
          origin: query.facets.origin?.join(','),
          source: query.facets.source?.join(','),
          destination: query.facets.destination?.join(','),
        },
        signal,
      ),
    [],
  );

  const openWithDetail = useCallback(
    (row: DispatchedDocumentListItem) => {
      dispatchedDocumentsApi
        .get(row.id)
        .then(setEditing)
        .catch((caught: unknown) =>
          toast.failure(caught instanceof ApiError ? caught.message : `Could not open ${row.trackingNumber}.`),
        );
    },
    [toast],
  );

  const rowActions = useCallback(
    (row: DispatchedDocumentListItem): MenuItem[] => [
      {
        key: `restore-${row.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: 'document-dispatching.delete',
        disabledReason: row.isActive ? 'This dispatch is already active.' : null,
        onClick: () => setConfirming({ kind: 'restore', row }),
      },
    ],
    [],
  );

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'delete') await dispatchedDocumentsApi.remove(confirming.row.id);
      else await dispatchedDocumentsApi.restore(confirming.row.id);

      toast.success(`${confirming.kind === 'delete' ? 'Deleted' : 'Restored'} ${confirming.row.trackingNumber}.`);
      setConfirming(null);
      refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0));
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <IndexPage<DispatchedDocumentListItem>
        module="document-dispatching"
        moduleNoun="dispatched documents"
        breadcrumb={['Document Management', 'Routing', 'Dispatching']}
        columns={COLUMNS}
        facets={[
          { key: 'source', label: 'Source' },
          { key: 'destination', label: 'To' },
          { key: 'documentType', label: 'Document Type' },
          { key: 'mode', label: 'Sent By' },
          { key: 'medium', label: 'Sent As' },
          { key: 'origin', label: 'From Unit' },
          { key: 'status', label: 'Status' },
        ]}
        auditEntityTypes={DISPATCHING_AUDIT_TYPES}
        rowKey={(row) => row.id}
        fetch={fetchRows}
        reloadKey={reloadKey}
        searchPlaceholder="Search by subject, tracking no., recipient, waybill or type"
        emptyMessage="Nothing dispatched yet. Record each document as it goes out — to a unit's Inbox or an outside office, sent on from Receiving or written by a unit."
        prerequisites={[
          {
            label: 'document types',
            met: hasType,
            to: `${ROUTING_SETTINGS_PATH}/document-types`,
            permission: 'routing-document-types.read',
          },
        ]}
        onCreate={() => setCreating(true)}
        createPermission="document-dispatching.create"
        onRowOpen={(row) => navigate(`${DISPATCHING_PATH}/${row.id}`)}
        openPermission="document-dispatching.read"
        rowActions={rowActions}
        onEdit={openWithDetail}
        editPermission="document-dispatching.update"
        onDelete={(row) => setConfirming({ kind: 'delete', row })}
        deletePermission="document-dispatching.delete"
        deleteLabel={() => 'Delete'}
        deleteDisabledReason={dispatchDeleteDisabledReason}
        otherInfo={(row) => row}
      />

      {creating || editing !== null ? (
        <DispatchedDocumentModal
          open
          editing={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={(saved) => {
            const created = editing === null;

            setCreating(false);
            setEditing(null);
            refresh();

            if (created) navigate(`${DISPATCHING_PATH}/${saved.id}`);
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={confirming.kind === 'delete' ? 'Delete dispatch' : 'Restore dispatch'}
          danger={confirming.kind === 'delete'}
          requireConfirm={confirming.kind === 'delete'}
          requireCredential={confirming.kind === 'delete'}
          loading={running}
          id={`dispatched-document-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              {confirming.kind === 'delete' ? (
                <>
                  <p>
                    {confirming.row.trackingNumber} — {confirming.row.subject} leaves the register. Its files are kept, and
                    it can be restored from this list.
                  </p>
                  {/* What it does to ANOTHER record, said before the tick. */}
                  {confirming.row.recipientOrgUnitId != null ? (
                    <p>It leaves {confirming.row.recipientOrgUnitName}&apos;s Inbox, which has not received it yet.</p>
                  ) : null}
                  {confirming.row.receivedDocumentId != null ? (
                    <p>
                      {confirming.row.receivedTrackingNumber} is put back on hand with{' '}
                      {confirming.row.originatingOrgUnitName}, as though it had not been dispatched.
                    </p>
                  ) : null}
                </>
              ) : (
                <p>
                  {confirming.row.trackingNumber} returns to the register.
                  {confirming.row.receivedDocumentId != null
                    ? ` ${confirming.row.receivedTrackingNumber} is dispatched again — refused if it has moved since.`
                    : ''}
                </p>
              )}

              {error ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

const COLUMNS: ColumnDef<DispatchedDocumentListItem>[] = [
  {
    key: 'subject',
    header: 'Document',
    width: 340,
    flex: true,
    sortable: true,
    render: (row) => <TextCell code={row.trackingNumber} value={row.subject} clamp />,
  },
  {
    key: 'documentTypeName',
    header: 'Type',
    width: 150,
    sortable: true,
    summary: true,
    render: (row) => <TextCell value={row.documentTypeName} />,
  },
  {
    // A unit of this organization, or the outside office as typed — one addressee, one value.
    key: 'recipient',
    header: 'To',
    width: 220,
    sortable: true,
    summary: true,
    render: (row) => <TextCell value={row.recipientOrgUnitName ?? row.recipientOffice} clamp />,
  },
  {
    key: 'originatingOrgUnitName',
    header: 'From Unit',
    width: 200,
    sortable: true,
    render: (row) => <TextCell value={row.originatingOrgUnitName} clamp />,
  },
  {
    key: 'mode',
    header: 'Sent By',
    width: 130,
    render: (row) => <TextCell value={row.modeLabel} />,
  },
  {
    key: 'medium',
    header: 'Sent As',
    width: 110,
    render: (row) => <TextCell value={row.mediumLabel} />,
  },
  {
    key: 'dispatchedAt',
    header: 'Dispatched',
    width: 170,
    sortable: true,
    summary: true,
    render: (row) => <DateCell value={row.dispatchedAt} />,
  },
  {
    // An em dash for a document a unit wrote — it sends on nothing.
    key: 'receivedTrackingNumber',
    header: 'Sends On',
    width: 140,
    render: (row) => <TextCell value={row.receivedTrackingNumber} mono />,
  },
  {
    key: 'attachmentCount',
    header: 'Files',
    width: 80,
    align: 'right',
    render: (row) => <TextCell value={String(row.attachmentCount)} mono numeric />,
  },
  {
    // In transit to a unit's Inbox, received there, or dispatched to an outside office.
    key: 'routing',
    header: 'Routing',
    width: 130,
    stage: true,
    render: (row) => <StatusText tone="inactive">{row.statusLabel}</StatusText>,
  },
  {
    key: 'status',
    header: 'Status',
    width: 110,
    status: true,
    render: (row) =>
      row.isActive ? <StatusText tone="active">Active</StatusText> : <StatusText tone="inactive">Deleted</StatusText>,
  },
];
